import * as Sentry from '@sentry/browser';
// @ts-expect-error foo
import { saveAs } from 'file-saver';
// @ts-expect-error foo
import { v4 as uuid } from 'uuid';
import { Element, ElementCompact, xml2js } from 'xml-js';

export function testUrlInDomains(url: string, domains: string[]) {
  if (domains.length === 0) return false;

  return new RegExp('^.*(' + domains.join('|') + ').*').test(url);
}

export function isEqual(a: any, b: any, seen = new WeakMap()): boolean {
  if (a === b) return true;

  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  if (a instanceof RegExp && b instanceof RegExp) {
    return a.source === b.source && a.flags === b.flags;
  }

  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) {
    return false;
  }

  if (seen.has(a)) return seen.get(a) === b;
  seen.set(a, b);

  if (a instanceof Set && b instanceof Set) {
    if (a.size !== b.size) return false;
    return [...a].every((val) => [...b].some((bVal) => isEqual(val, bVal, seen)));
  }

  if (a instanceof Map && b instanceof Map) {
    if (a.size !== b.size) return false;
    for (const [key, val] of a) {
      if (!b.has(key) || !isEqual(val, b.get(key), seen)) return false;
    }
    return true;
  }

  if (Array.isArray(a) !== Array.isArray(b)) return false;

  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);

  if (aKeys.length !== bKeys.length) return false;

  for (const key of aKeys) {
    if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
    if (!isEqual(a[key], b[key], seen)) return false;
  }

  return true;
}

export function isEmpty(value: unknown): boolean {
  if (value == null) return true; // null or undefined

  if (typeof value === 'string' || Array.isArray(value)) {
    return value.length === 0;
  }

  if (value instanceof Map || value instanceof Set) {
    return value.size === 0;
  }

  if (typeof value === 'object') {
    return Object.keys(value).length === 0;
  }

  return false; // numbers, booleans, functions, etc.
}

// @ts-expect-error foo
export function isArray(value) {
  return Array.isArray(value);
}

export function isObject(value: unknown) {
  if (isArray(value)) return false;

  return typeof value === 'object';
}

// @ts-expect-error foo
export function isUndefined(value): boolean {
  return value === undefined;
}

export function copyToClipboard(s: string): void {
  // Create new element
  const el = document.createElement('textarea');
  // Set value (string to be copied)
  el.value = s;
  // Set non-editable to avoid focus and move outside of view
  el.setAttribute('readonly', '');
  el.setAttribute('style', 'position: absolute;left: -9999px');
  document.body.appendChild(el);
  // Select text inside element
  el.select();
  // Copy text to clipboard
  document.execCommand('copy');
  // Remove temporary element
  document.body.removeChild(el);
}

export function hasKeyRecursive(obj: any, key: string): boolean {
  let found = false;

  for (const i in obj) {
    if (i === key) {
      found = true;
      break;
    }

    const o = obj[i];

    if (isObject(o)) {
      found = hasKeyRecursive(o, key);

      if (found) {
        break;
      }
    }

    if (isArray(o)) {
      for (const j of o) {
        const found = hasKeyRecursive(j, key);

        if (found) {
          return found;
        }
      }
    }
  }

  return found;
}

export function getValueByKeyRecursive(obj: any, key: string): any {
  let found = null;

  for (const i in obj) {
    if (i === key) {
      return obj[key];
    }

    const o = obj[i];

    if (isObject(o)) {
      found = getValueByKeyRecursive(o, key);

      if (found) {
        return found;
      }
    }

    if (isArray(o)) {
      for (const j of o) {
        const found = getValueByKeyRecursive(j, key);

        if (found) {
          return found;
        }
      }
    }
  }

  return null;
}

// @ts-expect-error foo
export function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}

export function saveFileFromUrl(url: string, filename = uniqueId(), format = 'png'): void {
  saveAs(url, `${filename}.${format}`);
}

export function uniqueId() {
  return uuid();
}

export function parseXML(xml: string): Element | ElementCompact {
  let parsed = null;

  try {
    parsed = xml2js(xml.replace(/&/g, '&amp;'), {
      alwaysChildren: true
    });
  } catch (e) {
    Sentry.captureException(e);
    // @ts-expect-error foo
    throw new Error(e);
  }

  return parsed;
}

export function getAppVersion(app: string): string {
  return `${app}@${require('../../../../../package.json').version}`;
}

export function isNil(value: unknown): value is null | undefined {
  return value == null;
}

export function cookiesAvailable() {
  let cookieEnabled = navigator.cookieEnabled;

  if (typeof navigator.cookieEnabled == 'undefined' && !cookieEnabled) {
    document.cookie = 'testcookie';
    cookieEnabled = document.cookie.indexOf('testcookie') != -1;
  }
  return cookieEnabled;
}

export function localeStorageAvailable() {
  const storage = window.localStorage;
  try {
    const x = '__storage_test__';
    storage.setItem(x, x);
    storage.removeItem(x);
    return true;
  } catch (e) {
    return (
      e instanceof DOMException &&
      // everything except Firefox
      (e.code === 22 ||
        // Firefox
        e.code === 1014 ||
        // test name field too, because code might not be present
        // everything except Firefox
        e.name === 'QuotaExceededError' ||
        // Firefox
        e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
      // acknowledge QuotaExceededError only if there's something already stored
      storage &&
      storage.length !== 0
    );
  }
}

export function cloneDeep<T>(value: T, seen = new WeakMap()): T {
  if (value === null || typeof value !== 'object') {
    return value;
  }

  if (seen.has(value)) {
    return seen.get(value);
  }

  let result: any;

  if (Array.isArray(value)) {
    result = [];
    seen.set(value, result);
    for (const item of value) {
      result.push(cloneDeep(item, seen));
    }
    return result;
  }

  if (value instanceof Date) {
    return new Date(value) as any;
  }

  if (value instanceof RegExp) {
    return new RegExp(value.source, value.flags) as any;
  }

  if (value instanceof Map) {
    result = new Map();
    seen.set(value, result);
    for (const [key, val] of value) {
      result.set(cloneDeep(key, seen), cloneDeep(val, seen));
    }
    return result;
  }

  if (value instanceof Set) {
    result = new Set();
    seen.set(value, result);
    for (const val of value) {
      result.add(cloneDeep(val, seen));
    }
    return result;
  }

  // Handle plain objects
  result = Object.create(Object.getPrototypeOf(value));
  seen.set(value, result);
  for (const key of Object.keys(value)) {
    result[key] = cloneDeep((value as any)[key], seen);
  }

  return result;
}

export function range(start: number, end?: number, step = 1): number[] {
  // If only one argument is provided, set end to start and start to 0
  if (end === undefined) {
    end = start;
    start = 0;
  }

  const result: number[] = [];

  if (step === 0) {
    throw new Error('Step cannot be 0');
  }

  if ((start < end && step > 0) || (start > end && step < 0)) {
    for (let i = start; step > 0 ? i < end : i > end; i += step) {
      result.push(i);
    }
  }

  return result;
}

export function merge<T extends object>(object: T, ...sources: object[]): T {
  // Clone the target object first to avoid mutation
  const result = { ...object };

  for (const source of sources) {
    if (source == null) continue;

    for (const [key, value] of Object.entries(source)) {
      const existingValue = (result as any)[key];

      if (Array.isArray(value)) {
        // If the value is an array, directly assign the array (override the existing value)
        (result as any)[key] = [...value];
      } else if (typeof existingValue === 'object' && typeof value === 'object') {
        // If both are objects, recursively merge them
        (result as any)[key] = merge(existingValue, value);
      } else {
        // Otherwise, directly assign the value from the source
        (result as any)[key] = value;
      }
    }
  }

  return result;
}

export function uniqBy<T>(array: T[], iteratee: ((value: T) => any) | string): T[] {
  const seen = new Set();
  const result: T[] = [];

  for (const item of array) {
    // If the iteratee is a string, use it to access the property
    const value = typeof iteratee === 'function' ? iteratee(item) : item[iteratee as keyof T];

    if (!seen.has(value)) {
      seen.add(value);
      result.push(item);
    }
  }

  return result;
}

export function difference<T>(array: T[], ...values: T[][]): T[] {
  // Flatten the array of arrays and create a Set of values to remove
  const valuesToRemove = new Set(values.flat());

  // Filter out the elements present in the Set
  return array.filter((item) => !valuesToRemove.has(item));
}

export function intersection<T>(...arrays: T[][]): T[] {
  // If no arrays are provided, return an empty array
  if (arrays.length === 0) return [];

  // Use the first array as the reference
  const [firstArray, ...restArrays] = arrays;

  // Create a Set from the first array for fast lookups
  const firstSet = new Set(firstArray);

  // Result array to store the intersection
  const result: T[] = [];

  // Filter out values from the first array that are present in all the other arrays
  for (const item of firstArray) {
    // If the item is in the set and appears in all the other arrays
    if (
      firstSet.has(item) &&
      restArrays.every((arr) => arr.includes(item)) &&
      !result.includes(item) // Ensure no duplicates in the result
    ) {
      result.push(item);
    }
  }

  return result;
}

export function omitBy<T>(object: Record<string, T>, predicate: (value: T, key: string) => boolean): Record<string, T> {
  const result: Record<string, T> = {};

  // Iterate over all object properties
  for (const key in object) {
    if (Object.prototype.hasOwnProperty.call(object, key)) {
      const value = object[key];

      // Apply the predicate function; if predicate returns false, include the property in the result
      if (!predicate(value, key)) {
        result[key] = value;
      }
    }
  }

  return result;
}

type Iteratee<T> = (value: T) => any;

export function sortBy<T>(array: T[], iteratee: Iteratee<T> | Iteratee<T>[]): T[] {
  if (Array.isArray(iteratee)) {
    return array.slice().sort((a, b) => {
      for (let i = 0; i < iteratee.length; i++) {
        const result = compareValues(iteratee[i](a), iteratee[i](b));
        if (result !== 0) {
          return result;
        }
      }
      return 0;
    });
  } else {
    return array.slice().sort((a, b) => compareValues(iteratee(a), iteratee(b)));
  }
}

// Helper function to compare two values in the sorting process
function compareValues(a: any, b: any): number {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}
